import {SpectatorData} from '@colonist/game-state-types'
import {LobbyImage} from '@colonist/types'
import {ArrayUtils} from '@colonist/utils'
import {ClientUserState} from '../../ClientUserState'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {createDivIn, createExternalImgIn, createImgIn, setElementInnerHTML} from '../../ui_utils/DomUtils'
import {getLobbyImagePath} from '../../ui_utils/Utils'
import {createTooltipIn} from '../../ui_utils/TooltipUtils.ts'

export default class UIGameSpectatorDisplay {
    private readonly spectatorContainer: HTMLDivElement
    private readonly avatarGroupContainer: HTMLDivElement

    constructor(spectatorContainer: HTMLDivElement) {
        this.spectatorContainer = spectatorContainer
        this.avatarGroupContainer = createDivIn(this.spectatorContainer, 'avatar-group-container', 'avatar-group-container')
        const spectatorEyeContainer = createDivIn(this.spectatorContainer, 'spectator-eye-container', '')
        createImgIn(spectatorEyeContainer, LobbyImage.Eye, 'spectator-eye-image')

        void this.updateSpectatorData([])
    }

    async updateSpectatorData(profileData: SpectatorData[]) {
        if(profileData.length <= 0) {
            this.setVisible(false)
            return
        }

        const swappedProfileData = this.setUserAsFirstSpectator(profileData)
        this.createAvatarGroup(swappedProfileData)
        this.setVisible(true)
    }

    private setUserAsFirstSpectator(profileData: SpectatorData[]): SpectatorData[] {
        const spectatorIndex = this.isUserSpectator(profileData)
        if(spectatorIndex == -1) return profileData

        return ArrayUtils.swapElementOfArray(profileData, 0, spectatorIndex)
    }

    // eslint-disable-next-line class-methods-use-this
    private isUserSpectator(profileData: SpectatorData[]): number {
        return profileData.findIndex((spectator) => spectator.username === ClientUserState.userState?.username)
    }

    private createAvatarGroup(profileData: SpectatorData[]) {
        setElementInnerHTML(this.avatarGroupContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        const maxAllowedAvatarsToDisplay = 3

        for(let index = 0; index < profileData.length; index++) {
            if(index >= maxAllowedAvatarsToDisplay) break
            this.createSpectatorAvatar(profileData[index])
        }

        this.createExtraSpectatorsAvatar(profileData, maxAllowedAvatarsToDisplay)
    }

    private createExtraSpectatorsAvatar(profileData: SpectatorData[], maxAllowedAvatarsToDisplay: number) {
        if(profileData.length <= maxAllowedAvatarsToDisplay) return

        const extraSpectators = profileData.slice(maxAllowedAvatarsToDisplay)
        const extraSpectatorsNames = extraSpectators.map((spectator) => spectator.username).join(' ')

        const extraSpectatorsContainer = createDivIn(this.avatarGroupContainer, 'extra-spectators-text', '')
        extraSpectatorsContainer.innerText = `+${extraSpectators.length}`
        createTooltipIn(extraSpectatorsContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: extraSpectatorsNames}})
    }

    private createSpectatorAvatar(profileData: SpectatorData) {
        const avatarClass = UIUtilsEjsVariables.isUserOnDiscord ? 'avatar-container-discord' : 'avatar-container'
        const tooltipContainer = createDivIn(this.avatarGroupContainer, `${avatarClass}`, '')
        createTooltipIn(tooltipContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: profileData.username}})

        const imagePath = profileData.profilePictureUrl ?? getLobbyImagePath(profileData.avatar)
        const avatarImageContainer = createDivIn(tooltipContainer, 'avatar-image-container')
        createExternalImgIn(avatarImageContainer, imagePath, {key: 'strings:storePage.sections.avatar.title'})
    }

    private setVisible(visible: boolean) {
        this.spectatorContainer.style.display = `${visible ? 'flex' : 'none'}`
    }
}
